package com.nnod.landing.nodin

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.PathEffect
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.nnod.landing.web.copyToClipboard
import com.nnod.ui.components.NnodCopyButton
import com.nnod.ui.components.NnodDisplayCodeBox
import com.nnod.ui.components.NnodGradientButton
import com.nnod.ui.components.NnodQrCode
import com.nnod.ui.theme.NnodIdolPillBg
import com.nnod.ui.theme.NnodIdolPillBorder
import com.nnod.ui.theme.NnodLandingCardBg
import com.nnod.ui.theme.NnodLandingCardBorder
import com.nnod.ui.theme.NnodLandingDivider
import com.nnod.ui.theme.NnodLandingInstructionsBg
import com.nnod.ui.theme.NnodLandingInstructionsBorder
import com.nnod.ui.theme.NnodPurpleDeep
import com.nnod.ui.theme.NnodPurpleLight
import com.nnod.ui.theme.NnodTextDark
import com.nnod.ui.theme.NnodTextLight
import com.nnod.ui.theme.NnodTextMid

@Composable
fun NodInEndScreen(
    kind: NodInLandingKind,
    isDesktop: Boolean,
    state: NodInEndState,
    labels: NodInEndLabels,
    modifier: Modifier = Modifier,
    onOpenApp: (String) -> Unit,
) {
    val theme = kind.toTheme()
    val bgGradient = Brush.verticalGradient(listOf(theme.bgFrom, theme.bgTo))
    val horizontalPadding = if (isDesktop) 24.dp else 16.dp

    Box(modifier = modifier.fillMaxSize().background(bgGradient)) {
        // Decorative circle — top-right, partially off-screen
        Box(
            modifier = Modifier
                .align(Alignment.TopEnd)
                .offset(y = (-80).dp)
                .size(280.dp)
                .clip(CircleShape)
                .background(
                    Brush.radialGradient(
                        colors = listOf(
                            theme.accentSoftColor.copy(alpha = 0.2f),
                            theme.accentColor.copy(alpha = 0.07f),
                        ),
                        center = Offset(280f * 0.35f, 280f * 0.35f),
                        radius = 280f * 0.7f,
                    )
                )
                .border(1.dp, theme.accentColor.copy(alpha = 0.13f), CircleShape)
        )

        // Decorative circle — bottom-left, partially off-screen
        Box(
            modifier = Modifier
                .align(Alignment.BottomStart)
                .offset(y = 100.dp)
                .size(200.dp)
                .clip(CircleShape)
                .background(
                    Brush.radialGradient(
                        colors = listOf(
                            theme.accentSoftColor.copy(alpha = 0.13f),
                            theme.accentColor.copy(alpha = 0.033f),
                        ),
                    )
                )
        )

        // Scrollable content column
        Column(
            modifier = Modifier
                .widthIn(max = 460.dp)
                .fillMaxWidth()
                .align(Alignment.TopCenter)
                .verticalScroll(rememberScrollState())
                .padding(
                    start = horizontalPadding,
                    end = horizontalPadding,
                    top = if (isDesktop) 56.dp else 32.dp,
                    bottom = 56.dp,
                ),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            NodInBrandRow(label = labels.pageTitle, theme = theme)
            Spacer(Modifier.height(18.dp))
            NodInStatusPill(badge = labels.badge, theme = theme)
            Spacer(Modifier.height(14.dp))

            when (val phase = state.phase) {
                is NodInEndPhase.Ready -> {
                    Text(
                        text = phase.campaignName,
                        fontSize = 26.sp,
                        fontWeight = FontWeight.ExtraBold,
                        color = NnodTextDark,
                        textAlign = TextAlign.Center,
                        lineHeight = 32.sp,
                        letterSpacing = (-0.3).sp,
                    )
                    Spacer(Modifier.height(10.dp))
                    NodInIdolPill(idolName = phase.idolName)
                    Spacer(Modifier.height(22.dp))
                    NodInMainCard(
                        ready = phase,
                        labels = labels,
                        theme = theme,
                        modifier = Modifier.fillMaxWidth(),
                    )
                    Spacer(Modifier.height(18.dp))
                    if (!isDesktop) {
                        NnodGradientButton(
                            onClick = { onOpenApp(phase.appDeepLink) },
                            modifier = Modifier.fillMaxWidth(),
                            gradientColors = listOf(theme.accentColor, theme.accentSoftColor),
                        ) {
                            Text(
                                text = labels.openAppButton,
                                fontSize = 16.sp,
                                fontWeight = FontWeight.ExtraBold,
                                color = Color.White,
                            )
                        }
                        Spacer(Modifier.height(18.dp))
                    }
                    NodInInstructionsPanel(
                        labels = labels,
                        theme = theme,
                        modifier = Modifier.fillMaxWidth(),
                    )
                }
                NodInEndPhase.NotFound -> {
                    Spacer(Modifier.height(8.dp))
                    NodInNotFoundCard(labels = labels, modifier = Modifier.fillMaxWidth())
                }
            }

            Spacer(Modifier.height(22.dp))
            Text(
                text = labels.footer,
                fontSize = 10.sp,
                color = NnodTextLight,
                fontWeight = FontWeight.SemiBold,
                textAlign = TextAlign.Center,
            )
        }
    }
}

@Composable
private fun NodInBrandRow(label: String, theme: NodInLandingTheme) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp),
    ) {
        Box(
            modifier = Modifier
                .size(32.dp)
                .clip(RoundedCornerShape(9.dp))
                .background(Brush.linearGradient(listOf(theme.accentColor, theme.accentSoftColor))),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = "N",
                fontSize = 15.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Color.White,
            )
        }
        Text(
            text = label,
            fontSize = 15.sp,
            fontWeight = FontWeight.ExtraBold,
            color = NnodTextDark,
            letterSpacing = 0.3.sp,
        )
    }
}

@Composable
private fun NodInStatusPill(badge: String, theme: NodInLandingTheme) {
    Row(
        modifier = Modifier
            .clip(CircleShape)
            .background(theme.statusBgColor)
            .padding(horizontal = 14.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(6.dp),
    ) {
        Box(
            modifier = Modifier
                .size(7.dp)
                .clip(CircleShape)
                .background(theme.statusDotColor)
        )
        Text(
            text = badge,
            fontSize = 11.sp,
            fontWeight = FontWeight.ExtraBold,
            color = theme.statusFgColor,
            letterSpacing = 1.2.sp,
        )
    }
}

@Composable
private fun NodInIdolPill(idolName: String) {
    Row(
        modifier = Modifier
            .clip(CircleShape)
            .background(NnodIdolPillBg)
            .border(1.dp, NnodIdolPillBorder, CircleShape)
            .padding(start = 4.dp, end = 14.dp, top = 4.dp, bottom = 4.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(26.dp)
                .clip(CircleShape)
                .background(Brush.linearGradient(listOf(NnodPurpleLight, NnodPurpleDeep))),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = idolName.take(1),
                fontSize = 12.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Color.White,
            )
        }
        Text(
            text = idolName,
            fontSize = 13.sp,
            fontWeight = FontWeight.Bold,
            color = NnodTextDark,
        )
    }
}

@Composable
private fun NodInMainCard(
    ready: NodInEndPhase.Ready,
    labels: NodInEndLabels,
    theme: NodInLandingTheme,
    modifier: Modifier = Modifier,
) {
    BoxWithConstraints(modifier = modifier) {
        val compact = maxWidth < 340.dp
        val cardPadding = if (compact) 16.dp else 24.dp
        val qrSize = if (compact) 196.dp else 220.dp
        val codeCellWidth = if (compact) 27.dp else 34.dp
        val codeCellHeight = if (compact) 38.dp else 44.dp
        val codeCellSpacing = if (compact) 4.dp else 6.dp
        val copyButtonSize = if (compact) 38.dp else 42.dp

        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clip(RoundedCornerShape(28.dp))
                .background(NnodLandingCardBg)
                .border(1.5.dp, NnodLandingCardBorder, RoundedCornerShape(28.dp))
                .padding(cardPadding),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Time row
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.Center,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(
                    text = labels.endTimeLabel,
                    fontSize = 11.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = NnodTextMid,
                    letterSpacing = 1.sp,
                )
                Spacer(Modifier.width(8.dp))
                Text(
                    text = ready.scheduledEndTime,
                    fontSize = 14.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = NnodTextDark,
                )
            }

            Spacer(Modifier.height(16.dp))

            // Dashed divider
            Canvas(modifier = Modifier.fillMaxWidth().height(1.dp)) {
                drawLine(
                    color = NnodLandingDivider,
                    start = Offset(0f, 0f),
                    end = Offset(size.width, 0f),
                    strokeWidth = 1.dp.toPx(),
                    pathEffect = PathEffect.dashPathEffect(floatArrayOf(8f, 4f), 0f),
                    cap = StrokeCap.Round,
                )
            }

            Spacer(Modifier.height(16.dp))

            // QR code in white padded box
            Box(
                modifier = Modifier
                    .clip(RoundedCornerShape(18.dp))
                    .background(Color.White)
                    .border(1.dp, NnodLandingDivider, RoundedCornerShape(18.dp))
                    .padding(12.dp),
                contentAlignment = Alignment.Center,
            ) {
                NnodQrCode(payload = ready.qrPayload, size = qrSize)
            }

            Spacer(Modifier.height(18.dp))

            // "または" divider
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                HorizontalDivider(modifier = Modifier.weight(1f), color = NnodLandingDivider, thickness = 1.dp)
                Text(
                    text = labels.orDivider,
                    modifier = Modifier.padding(horizontal = 12.dp),
                    fontSize = 10.sp,
                    fontWeight = FontWeight.ExtraBold,
                    color = NnodTextMid,
                    letterSpacing = 1.4.sp,
                )
                HorizontalDivider(modifier = Modifier.weight(1f), color = NnodLandingDivider, thickness = 1.dp)
            }

            Spacer(Modifier.height(14.dp))

            Text(
                text = labels.codeInputLabel,
                fontSize = 11.sp,
                fontWeight = FontWeight.ExtraBold,
                color = NnodTextMid,
                letterSpacing = 1.2.sp,
            )

            Spacer(Modifier.height(12.dp))

            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(if (compact) 8.dp else 10.dp),
            ) {
                NnodDisplayCodeBox(
                    code = ready.displayCode,
                    accentColor = theme.accentColor,
                    cellWidth = codeCellWidth,
                    cellHeight = codeCellHeight,
                    cellSpacing = codeCellSpacing,
                )
                NnodCopyButton(
                    onCopy = { copyToClipboard(ready.displayCode) },
                    accentColor = theme.accentColor,
                    modifier = Modifier.size(copyButtonSize),
                )
            }
        }
    }
}

@Composable
private fun NodInInstructionsPanel(
    labels: NodInEndLabels,
    theme: NodInLandingTheme,
    modifier: Modifier = Modifier,
) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(18.dp))
            .background(NnodLandingInstructionsBg)
            .border(1.dp, NnodLandingInstructionsBorder, RoundedCornerShape(18.dp))
            .padding(horizontal = 20.dp, vertical = 16.dp),
        verticalArrangement = Arrangement.spacedBy(4.dp),
    ) {
        Text(
            text = labels.instructionsTitle,
            fontSize = 13.sp,
            fontWeight = FontWeight.ExtraBold,
            color = NnodTextDark,
        )
        Spacer(Modifier.height(4.dp))
        NodInInstructionStep(number = "1", text = labels.instructionsStep1, theme = theme)
        NodInInstructionStep(number = "2", text = labels.instructionsStep2, theme = theme)
    }
}

@Composable
private fun NodInInstructionStep(number: String, text: String, theme: NodInLandingTheme) {
    Row(
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(8.dp),
    ) {
        Box(
            modifier = Modifier
                .size(18.dp)
                .clip(CircleShape)
                .background(theme.accentColor),
            contentAlignment = Alignment.Center,
        ) {
            Text(
                text = number,
                fontSize = 10.sp,
                lineHeight = 10.sp,
                fontWeight = FontWeight.ExtraBold,
                color = Color.White,
                textAlign = TextAlign.Center,
            )
        }
        Text(
            text = text,
            fontSize = 12.sp,
            color = NnodTextMid,
            lineHeight = 18.sp,
        )
    }
}

@Composable
private fun NodInNotFoundCard(labels: NodInEndLabels, modifier: Modifier = Modifier) {
    Column(
        modifier = modifier
            .clip(RoundedCornerShape(28.dp))
            .background(NnodLandingCardBg)
            .border(1.5.dp, NnodLandingCardBorder, RoundedCornerShape(28.dp))
            .padding(32.dp),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(16.dp),
    ) {
        Text(
            text = labels.notFoundTitle,
            fontSize = 16.sp,
            fontWeight = FontWeight.Bold,
            color = NnodTextDark,
            textAlign = TextAlign.Center,
        )
        Text(
            text = labels.notFoundBody,
            fontSize = 13.sp,
            color = NnodTextLight,
            textAlign = TextAlign.Center,
        )
    }
}
